import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Loader, LogOut, Stethoscope, Bot, Database, Cpu, ThumbsUp, ThumbsDown, MessageSquare, MoreHorizontal, Pencil, Star, Menu, X } from 'lucide-react';
import { 
  queryRAG, 
  getUserChats, 
  createChat, 
  updateChatTitle, 
  getChatMessages, 
  saveMessage,
  submitMessageFeedback 
} from '../services/api';

export const AnalysisWorkspace = ({ user, onLogout }) => {
  const [messages, setMessages] = useState([]);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingChats, setLoadingChats] = useState(true);
  const [showSources, setShowSources] = useState(null);
  const [useRag, setUseRag] = useState(true);
  const [feedback, setFeedback] = useState({});
  const [feedbackNoteOpen, setFeedbackNoteOpen] = useState(null);
  const [feedbackNoteText, setFeedbackNoteText] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [editingChatId, setEditingChatId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [hoveredChatId, setHoveredChatId] = useState(null);
  const [menuOpenChatId, setMenuOpenChatId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [serverUnavailable, setServerUnavailable] = useState(false);
  const messagesEndRef = useRef(null);
  const titleInputRef = useRef(null);

  const displayName =
    [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() ||
    user?.username ||
    user?.email ||
    'User';
  const displayInitial = (displayName?.charAt(0) || 'U').toUpperCase();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Load chats from database when user logs in
  const loadingRef = useRef(false);
  const loadUserChats = useCallback(async () => {
    if (!user?.username) return;
    if (loadingRef.current) return;
    loadingRef.current = true;
    
    setLoadingChats(true);
    try {
      const chats = await getUserChats(user.username);
      
      if (chats && chats.length > 0) {
        setChatHistory(chats.map(c => ({
          id: c.id.toString(),
          title: c.title,
          active: false,
          dbId: c.id
        })));
        
        // Select the first (most recent) chat
        const firstChat = chats[0];
        setActiveChatId(firstChat.id.toString());
        setChatHistory(prev => prev.map(c => ({
          ...c,
          active: c.id === firstChat.id.toString()
        })));
        
        // Load messages for the first chat
        await loadChatMessages(firstChat.id);
      } else {
        // No chats, create a new one
        await handleNewChat();
      }
    } catch (err) {
      console.error('Failed to load chats:', err);
      if (!err.response || err.code === 'ERR_NETWORK') {
        setServerUnavailable(true);
      }
      // Fallback to local state
      setChatHistory([{ id: 'local-1', title: 'New Chat', active: true }]);
      setActiveChatId('local-1');
      setMessages([{
        id: '0',
        role: 'assistant',
        content: 'Hello! How can I help you today?',
        timestamp: new Date(),
      }]);
    } finally {
      setLoadingChats(false);
      loadingRef.current = false;
    }
  }, [user?.username]);

  // Load messages for a specific chat
  const loadChatMessages = async (chatId) => {
    try {
      const dbMessages = await getChatMessages(chatId);
      
      if (dbMessages && dbMessages.length > 0) {
        const loadedMessages = dbMessages.map(m => ({
          id: m.id.toString(),
          role: m.role,
          content: m.content,
          sources: m.sources,
          timestamp: new Date(m.created_at),
          dbId: m.id
        }));
        setMessages(loadedMessages);
        
        // Load feedback state
        const feedbackState = {};
        dbMessages.forEach(m => {
          if (m.feedback) {
            feedbackState[m.id.toString()] = {
              rating: m.feedback.rating,
              star_rating: m.feedback.star_rating,
              note: m.feedback.note
            };
          }
        });
        setFeedback(feedbackState);
      } else {
        // No messages, show welcome message
        setMessages([{
          id: '0',
          role: 'assistant',
          content: 'Hello! How can I help you today?',
          timestamp: new Date(),
        }]);
        setFeedback({});
      }
    } catch (err) {
      console.error('Failed to load messages:', err);
      setMessages([{
        id: '0',
        role: 'assistant',
        content: 'Hello! How can I help you today?',
        timestamp: new Date(),
      }]);
    }
  };

  // Load chats on mount
  useEffect(() => {
    loadUserChats();
  }, [loadUserChats]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!menuOpenChatId) return;
    
    const handleClickOutside = (e) => {
      const menuElement = document.querySelector('[data-chat-menu]');
      if (menuElement && !menuElement.contains(e.target)) {
        setMenuOpenChatId(null);
      }
    };
    
    setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [menuOpenChatId]);

  const handleNewChat = async () => {
    try {
      // Create chat in database
      const newChat = await createChat(user.username, 'New Chat');
      
      const newChatId = newChat.id.toString();
      setChatHistory(prev => [{
        id: newChatId,
        title: newChat.title,
        active: true,
        dbId: newChat.id
      }, ...prev.map(c => ({ ...c, active: false }))]);
      
      setActiveChatId(newChatId);
      setMessages([{
        id: '0',
        role: 'assistant',
        content: 'Hello! How can I help you today?',
        timestamp: new Date(),
      }]);
      setFeedback({});
    } catch (err) {
      console.error('Failed to create chat:', err);
      // Fallback to local
      const newChatId = Date.now().toString();
      setChatHistory(prev => [{
        id: newChatId,
        title: 'New Chat',
        active: true
      }, ...prev.map(c => ({ ...c, active: false }))]);
      setActiveChatId(newChatId);
      setMessages([{
        id: '0',
        role: 'assistant',
        content: 'Hello! How can I help you today?',
        timestamp: new Date(),
      }]);
    }
  };

  const handleSubmit = async (query) => {
    if (!query.trim() || loading) return;
    setSuggestedQuestions([]);

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: query,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    
    // Auto-generate title from first user message
    const currentChat = chatHistory.find(c => c.id === activeChatId);
    if (currentChat && currentChat.title === 'New Chat') {
      const autoTitle = query.length > 30 ? query.substring(0, 30) + '...' : query;
      setChatHistory(prev => prev.map(c => 
        c.id === activeChatId ? { ...c, title: autoTitle } : c
      ));
      
      // Update title in database
      try {
        const dbChatId = currentChat.dbId || parseInt(activeChatId);
        if (!isNaN(dbChatId)) {
          await updateChatTitle(dbChatId, autoTitle);
        }
      } catch (err) {
        console.error('Failed to update chat title:', err);
      }
    }
    
    // Save user message to database
    try {
      const dbChatId = currentChat?.dbId || parseInt(activeChatId);
      if (!isNaN(dbChatId)) {
        const savedUserMsg = await saveMessage(dbChatId, {
          role: 'user',
          content: query,
          mode: useRag ? 'rag' : 'llm_only'
        });
        userMessage.dbId = savedUserMsg.id;
      }
    } catch (err) {
      console.error('Failed to save user message:', err);
    }
    
    setInput('');
    setLoading(true);

    try {
      const response = await queryRAG({ 
        question: query, 
        top_k: 5,
        conversation_history: messages.filter(m => m.role !== 'system').map(m => ({
          role: m.role,
          content: m.content
        })),
        use_rag: useRag
      });

      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.answer || 'No answer provided',
        sources: response.sources,
        timestamp: new Date(),
      };
      
      // Debug: Log greeting detection
      if (query.toLowerCase().trim() === 'hello' || query.toLowerCase().trim() === 'hi' || query.toLowerCase().trim() === 'merhaba') {
        console.log('Greeting detected! Response:', response.answer);
        console.log('Suggested questions:', response.suggested_questions);
      }
      
      if (Array.isArray(response.suggested_questions) && response.suggested_questions.length > 0) {
        setSuggestedQuestions(response.suggested_questions.slice(0, 3));
      } else {
        setSuggestedQuestions([]);
      }

      setServerUnavailable(false);

      // Save assistant message to database (reply_to_message_id = user message id)
      try {
        const dbChatId = currentChat?.dbId || parseInt(activeChatId);
        if (!isNaN(dbChatId)) {
          const savedAssistantMsg = await saveMessage(dbChatId, {
            role: 'assistant',
            content: response.answer,
            mode: useRag ? 'rag' : 'llm_only',
            sources: response.sources,
            reply_to_message_id: userMessage.dbId || null
          });
          assistantMessage.dbId = savedAssistantMsg.id;
        }
      } catch (err) {
        console.error('Failed to save assistant message:', err);
      }

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err) {
      const isServerUnavailable = !err.response || err.code === 'ERR_NETWORK';
      const content = isServerUnavailable
        ? 'Server not available. Please check your connection and try again.'
        : 'Sorry, an error occurred. Please try again.';
      if (isServerUnavailable) setServerUnavailable(true);
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestedQuestionClick = async (q) => {
    if (!q || loading) return;
    await handleSubmit(q);
  };

  const toggleSources = (messageId) => {
    setShowSources(showSources === messageId ? null : messageId);
  };

  const handleFeedback = async (messageId, rating, messageDbId) => {
    // Update local state
    setFeedback(prev => ({
      ...prev,
      [messageId]: { ...prev[messageId], rating }
    }));

    // Send to database
    try {
      if (messageDbId) {
        await submitMessageFeedback(
          messageDbId, 
          rating, 
          feedback[messageId]?.star_rating || null,
          feedback[messageId]?.note || null
        );
      }
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    }
  };

  const handleStarRating = async (messageId, stars, messageDbId) => {
    // Update local state
    setFeedback(prev => ({
      ...prev,
      [messageId]: { ...prev[messageId], star_rating: stars }
    }));

    // Send to database
    try {
      if (messageDbId) {
        await submitMessageFeedback(
          messageDbId, 
          feedback[messageId]?.rating || 'neutral',
          stars,
          feedback[messageId]?.note || null
        );
      }
    } catch (err) {
      console.error('Failed to submit star rating:', err);
    }
  };

  const handleFeedbackNote = async (messageId, messageDbId) => {
    if (!feedbackNoteText.trim()) return;
    
    // Update local state
    setFeedback(prev => ({
      ...prev,
      [messageId]: { ...prev[messageId], note: feedbackNoteText }
    }));

    // Send to database
    try {
      if (messageDbId) {
        await submitMessageFeedback(
          messageDbId, 
          feedback[messageId]?.rating || 'neutral',
          feedback[messageId]?.star_rating || null,
          feedbackNoteText
        );
      }
    } catch (err) {
      console.error('Failed to submit feedback note:', err);
    }

    setFeedbackNoteOpen(null);
    setFeedbackNoteText('');
  };

  const selectChat = async (chatId) => {
    setActiveChatId(chatId);
    setChatHistory(prev => prev.map(c => ({ ...c, active: c.id === chatId })));
    
    // Load messages for selected chat
    const chat = chatHistory.find(c => c.id === chatId);
    if (chat?.dbId) {
      await loadChatMessages(chat.dbId);
    }
  };

  const startEditingTitle = (chatId, currentTitle) => {
    setEditingChatId(chatId);
    setEditingTitle(currentTitle);
    setMenuOpenChatId(null);
    setTimeout(() => {
      titleInputRef.current?.focus();
      titleInputRef.current?.select();
    }, 0);
  };

  const saveTitle = async (chatId) => {
    if (editingTitle.trim()) {
      setChatHistory(prev => prev.map(c => 
        c.id === chatId ? { ...c, title: editingTitle.trim() } : c
      ));
      
      // Update in database
      const chat = chatHistory.find(c => c.id === chatId);
      if (chat?.dbId) {
        try {
          await updateChatTitle(chat.dbId, editingTitle.trim());
        } catch (err) {
          console.error('Failed to update title:', err);
        }
      }
    }
    setEditingChatId(null);
    setEditingTitle('');
  };

  // Show loading while loading chats
  if (loadingChats) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #1a1f36 0%, #0d1117 100%)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Loader size={40} color="#3B82F6" style={{ animation: 'spin 1s linear infinite' }} />
          <p style={{ color: '#8b95a5', marginTop: '16px' }}>Loading your chats...</p>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      height: '100vh', 
      overflow: 'hidden',
      fontFamily: "'Inter', sans-serif",
      backgroundColor: '#f4f7fb',
      position: 'relative'
    }}>
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          onClick={() => setSidebarOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
            display: 'none'
          }}
          className="mobile-overlay"
        />
      )}

      {/* Sidebar */}
      <aside style={{
        width: '288px',
        background: 'linear-gradient(180deg, #0a192f 0%, #001e3c 100%)',
        color: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        position: 'relative',
        zIndex: 1000,
        transition: 'transform 0.3s ease'
      }}
      className={`sidebar ${sidebarOpen ? 'open' : ''}`}
      >
        {/* Close button (mobile only) */}
        <button
          onClick={() => setSidebarOpen(false)}
          className="sidebar-close-btn"
          style={{
            display: 'none'
          }}
          title="Close menu"
        >
          <X size={20} />
        </button>

        {/* Logo Area */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px'
        }}>
          <img 
            src="/Aiatus_logo.png" 
            alt="Aiatus AI" 
            style={{ height: '70px', objectFit: 'contain' }}
          />
          <p style={{ 
            fontSize: '15px', 
            color: '#93c5fd', 
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.3px',
            margin: 0
          }}>
            Smart assistant for Pediatric Transplantation
          </p>
        </div>

        {/* New Chat & History */}
        <div style={{ padding: '16px', flex: 1, display: 'flex', flexDirection: 'column', gap: '16px', overflow: 'hidden' }}>
          <button 
            onClick={handleNewChat}
            style={{
              width: '100%',
              backgroundColor: '#2563eb',
              padding: '12px',
              borderRadius: '12px',
              fontWeight: 600,
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: '0 10px 15px -3px rgba(30, 64, 175, 0.5)',
              transition: 'all 0.2s'
            }}
          >
            <span style={{ fontSize: '20px' }}>+</span> New Chat
          </button>

          <nav style={{ display: 'flex', flexDirection: 'column', gap: '4px', overflowY: 'auto', flex: 1 }}>
            <p style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'bold', textTransform: 'uppercase', padding: '0 16px', marginBottom: '8px' }}>
              Recent Queries
            </p>
            {chatHistory.map((chat) => (
              <div
                key={chat.id}
                onClick={() => {
                  if (editingChatId !== chat.id && menuOpenChatId !== chat.id) {
                    selectChat(chat.id);
                  }
                }}
                onMouseEnter={() => setHoveredChatId(chat.id)}
                onMouseLeave={() => {
                  if (menuOpenChatId !== chat.id) {
                    setHoveredChatId(null);
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  background: chat.active ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                  transition: 'all 0.2s',
                  position: 'relative'
                }}
              >
                <span style={{ opacity: 0.5, fontStyle: 'italic' }}>#</span>
                {editingChatId === chat.id ? (
                  <input
                    ref={titleInputRef}
                    type="text"
                    value={editingTitle}
                    onChange={(e) => setEditingTitle(e.target.value)}
                    onBlur={() => saveTitle(chat.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        saveTitle(chat.id);
                      } else if (e.key === 'Escape') {
                        setEditingChatId(null);
                        setEditingTitle('');
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      flex: 1,
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '4px 8px',
                      color: 'white',
                      fontSize: '16px',
                      outline: 'none'
                    }}
                  />
                ) : (
                  <>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {chat.title}
                    </span>
                    
                    {/* Three dots menu button */}
                    {(hoveredChatId === chat.id || menuOpenChatId === chat.id) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuOpenChatId(menuOpenChatId === chat.id ? null : chat.id);
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          padding: '4px',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'rgba(255, 255, 255, 0.6)',
                          transition: 'all 0.2s'
                        }}
                      >
                        <MoreHorizontal size={16} />
                      </button>
                    )}

                    {/* Dropdown menu */}
                    {menuOpenChatId === chat.id && (
                      <div
                        data-chat-menu
                        style={{
                          position: 'absolute',
                          top: '100%',
                          right: '8px',
                          marginTop: '4px',
                          background: '#1e293b',
                          borderRadius: '8px',
                          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          zIndex: 100,
                          minWidth: '140px',
                          overflow: 'hidden'
                        }}
                      >
                        {/* Rename option */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditingTitle(chat.id, chat.title);
                          }}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 14px',
                            background: 'transparent',
                            border: 'none',
                            color: 'white',
                            fontSize: '15px',
                            cursor: 'pointer',
                            textAlign: 'left',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
                          onMouseLeave={(e) => e.target.style.background = 'transparent'}
                        >
                          <Pencil size={14} />
                          Rename
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </nav>
        </div>

        {/* Sidebar bottom logos: alt alta, Cerrahpaşa üstte (logo + yazı), sola hizalı */}
        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          gap: '12px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '14px'
          }}>
            <img
              src="/cerrahpasa_tip_logo.png"
              alt="Cerrahpaşa Tıp"
              style={{ height: '82px', objectFit: 'contain', flexShrink: 0 }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', justifyContent: 'center' }}>
              <span style={{
                color: '#E8C547',
                fontSize: '13px',
                fontWeight: 500,
                lineHeight: 1.2
              }}>
                İstanbul Üniversitesi-Cerrahpaşa
              </span>
              <span style={{
                color: '#FFFFFF',
                fontSize: '15px',
                fontWeight: 700,
                lineHeight: 1.2
              }}>
                Tıp Fakültesi
              </span>
            </div>
          </div>
          <img
            src="/certain_logo.png"
            alt="CERTAIN"
            style={{ height: '64px', objectFit: 'contain', marginLeft: '6px' }}
          />
        </div>

        {/* User Tag */}
        <div style={{
          padding: '16px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'rgba(0, 0, 0, 0.2)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            background: 'rgba(255, 255, 255, 0.05)',
            padding: '12px',
            borderRadius: '16px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              backgroundColor: '#3b82f6',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: '16px'
            }}>
              {displayInitial}
            </div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <p style={{ fontSize: '16px', fontWeight: 'bold', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {displayName}
              </p>
            </div>
            {onLogout && (
              <button
                onClick={onLogout}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: '8px',
                  cursor: 'pointer',
                  color: 'rgba(255, 255, 255, 0.6)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Logout"
              >
                <LogOut size={18} />
              </button>
            )}
          </div>
        </div>
      </aside>

      {/* Main Chat Area */}
      <main style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#F8FAFC',
        minWidth: 0
      }}>
        {/* Header */}
        <header style={{
          height: '96px',
          backgroundColor: 'white',
          borderBottom: '1px solid #e2e8f0',
          padding: '0 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Mobile Menu Button */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              style={{
                background: 'transparent',
                border: 'none',
                padding: '8px',
                cursor: 'pointer',
                borderRadius: '8px',
                display: 'none',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#64748b'
              }}
              className="mobile-menu-btn"
              title="Toggle menu"
            >
              <Menu size={24} />
            </button>
            <div style={{
              padding: '10px',
              backgroundColor: '#eff6ff',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#2563eb'
            }}>
              <Stethoscope size={22} />
            </div>
            <h2 style={{ fontWeight: 'bold', color: '#1e293b', margin: 0, fontSize: '22px' }}>LearnPedTx</h2>
          </div>
          
          {/* RAG/LLM Toggle */}
          <div className="rag-toggle" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '4px',
            backgroundColor: '#f1f5f9',
            padding: '4px',
            borderRadius: '10px'
          }}>
            <button
              type="button"
              onClick={() => setUseRag(true)}
              title="RAG + LLM: Uses document retrieval"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: useRag ? 'white' : 'transparent',
                color: useRag ? '#2563eb' : '#64748b',
                fontWeight: 600,
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: useRag ? '0 2px 8px rgba(0, 0, 0, 0.08)' : 'none'
              }}
            >
              <Database size={14} />
              RAG + LLM
            </button>
            
            <button
              type="button"
              onClick={() => setUseRag(false)}
              title="LLM Only: Full document context"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: !useRag ? 'white' : 'transparent',
                color: !useRag ? '#8b5cf6' : '#64748b',
                fontWeight: 600,
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: !useRag ? '0 2px 8px rgba(0, 0, 0, 0.08)' : 'none'
              }}
            >
              <Cpu size={14} />
              LLM Only
            </button>
          </div>
          
          {/* Partner Logos removed (Aiatus only) */}
          <div
            className="topbar-logos"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '18px',
            }}
          >
            <img
              src="/Aiatus_logo.png"
              alt="Aiatus Logo"
              style={{ height: '45px', objectFit: 'contain' }}
            />
            <img
              src="/cerrahpasa_tip_logo.png"
              alt="Cerrahpaşa Tıp Logo"
              style={{ height: '85px', objectFit: 'contain' }}
            />
            <img
              src="/certain_logo.png"
              alt="Certain Logo"
              style={{ height: '60px', objectFit: 'contain' }}
            />
          </div>
        </header>

        {/* Server unavailable banner */}
        {serverUnavailable && (
          <div style={{
            padding: '12px 24px',
            backgroundColor: '#fef3c7',
            borderBottom: '1px solid #f59e0b',
            color: '#92400e',
            fontSize: '15px',
            fontWeight: 500,
            textAlign: 'center'
          }}>
            Server not available. Please check your connection and try again.
          </div>
        )}

        {/* Chat Messages */}
        <div className="chat-messages" style={{
          flex: 1,
          overflowY: 'auto',
          padding: '32px',
          display: 'flex',
          flexDirection: 'column',
          gap: '32px'
        }}>
          {messages.map((message) => (
            <div 
              key={message.id}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '16px',
                maxWidth: '900px',
                alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
                flexDirection: message.role === 'user' ? 'row-reverse' : 'row'
              }}
            >
              <div style={{
                width: '36px',
                height: '36px',
                backgroundColor: message.role === 'user' ? '#3b82f6' : '#dbeafe',
                color: message.role === 'user' ? 'white' : '#2563eb',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                {message.role === 'user' ? displayInitial : <Bot size={18} />}
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{
                  backgroundColor: message.role === 'user' ? '#2563eb' : 'white',
                  color: message.role === 'user' ? 'white' : '#334155',
                  padding: message.role === 'user' ? '10px 16px' : '20px',
                  borderRadius: message.role === 'user' ? '12px' : '16px',
                  borderTopLeftRadius: message.role === 'user' ? '12px' : '4px',
                  borderTopRightRadius: message.role === 'user' ? '4px' : '16px',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
                  border: message.role === 'user' ? 'none' : '1px solid #f1f5f9',
                  lineHeight: 1.7,
                  fontSize: '18px'
                }}>
                  <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{message.content}</p>
                </div>
                
                {/* Suggested questions (from retrieved context) */}
                {message.role === 'assistant' &&
                  message.id === messages[messages.length - 1]?.id &&
                  suggestedQuestions.length > 0 && (
                    <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ 
                        fontSize: '15px', 
                        color: '#64748b', 
                        fontWeight: 500,
                        marginBottom: '4px'
                      }}>
                        Suggested questions you can ask:
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {suggestedQuestions.map((q, idx) => (
                          <button
                            key={`${idx}-${q}`}
                            onClick={() => handleSuggestedQuestionClick(q)}
                            disabled={loading}
                            style={{
                              background: '#f8fafc',
                              border: '1px solid #e2e8f0',
                              borderRadius: '999px',
                              padding: '6px 12px',
                              fontSize: '14px',
                              color: '#334155',
                              cursor: loading ? 'not-allowed' : 'pointer',
                              transition: 'all 0.2s',
                              whiteSpace: 'nowrap',
                            }}
                            onMouseEnter={(e) => {
                              if (!loading) {
                                e.target.style.background = '#e2e8f0';
                                e.target.style.borderColor = '#cbd5e1';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!loading) {
                                e.target.style.background = '#f8fafc';
                                e.target.style.borderColor = '#e2e8f0';
                              }
                            }}
                            title="Ask this question"
                          >
                            {q}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}


                {/* Feedback buttons - only for assistant messages */}
                {message.role === 'assistant' && message.id !== '0' && (
                  <div className="feedback-buttons" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                    {/* Thumbs Up */}
                    <button
                      onClick={() => handleFeedback(message.id, 'good', message.dbId)}
                      style={{
                        background: feedback[message.id]?.rating === 'good' ? '#dcfce7' : 'transparent',
                        border: feedback[message.id]?.rating === 'good' ? '1px solid #22c55e' : '1px solid #e2e8f0',
                        borderRadius: '6px',
                        padding: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s'
                      }}
                      title="Good response"
                    >
                      <ThumbsUp size={14} color={feedback[message.id]?.rating === 'good' ? '#22c55e' : '#94a3b8'} />
                    </button>

                    {/* Thumbs Down */}
                    <button
                      onClick={() => handleFeedback(message.id, 'bad', message.dbId)}
                      style={{
                        background: feedback[message.id]?.rating === 'bad' ? '#fee2e2' : 'transparent',
                        border: feedback[message.id]?.rating === 'bad' ? '1px solid #ef4444' : '1px solid #e2e8f0',
                        borderRadius: '6px',
                        padding: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s'
                      }}
                      title="Bad response"
                    >
                      <ThumbsDown size={14} color={feedback[message.id]?.rating === 'bad' ? '#ef4444' : '#94a3b8'} />
                    </button>

                    {/* Star Rating */}
                    <div className="star-rating" style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '4px', 
                      marginLeft: '8px',
                      padding: '4px 8px',
                      background: 'rgba(0, 0, 0, 0.02)',
                      borderRadius: '8px'
                    }}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => handleStarRating(message.id, star, message.dbId)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            padding: '2px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            transition: 'all 0.2s'
                          }}
                          title={`${star} star${star > 1 ? 's' : ''}`}
                        >
                          <Star 
                            size={16} 
                            fill={feedback[message.id]?.star_rating >= star ? '#fbbf24' : 'transparent'}
                            color={feedback[message.id]?.star_rating >= star ? '#fbbf24' : '#cbd5e1'}
                            strokeWidth={2}
                          />
                        </button>
                      ))}
                    </div>

                    {/* Add Note Button */}
                    <button
                      onClick={() => {
                        setFeedbackNoteOpen(feedbackNoteOpen === message.id ? null : message.id);
                        setFeedbackNoteText(feedback[message.id]?.note || '');
                      }}
                      style={{
                        background: feedback[message.id]?.note ? '#e0f2fe' : 'transparent',
                        border: feedback[message.id]?.note ? '1px solid #0ea5e9' : '1px solid #e2e8f0',
                        borderRadius: '6px',
                        padding: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s'
                      }}
                      title="Add note"
                    >
                      <MessageSquare size={14} color={feedback[message.id]?.note ? '#0ea5e9' : '#94a3b8'} />
                    </button>

                    {/* Feedback submitted indicator */}
                    {(feedback[message.id]?.rating || feedback[message.id]?.star_rating) && (
                      <span style={{ fontSize: '12px', color: '#94a3b8', marginLeft: '4px' }}>
                        Feedback saved
                      </span>
                    )}
                  </div>
                )}

                {/* Note Input */}
                {feedbackNoteOpen === message.id && (
                  <div style={{
                    marginTop: '8px',
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'flex-start'
                  }}>
                    <textarea
                      value={feedbackNoteText}
                      onChange={(e) => setFeedbackNoteText(e.target.value)}
                      placeholder="Add your feedback note..."
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        fontSize: '15px',
                        resize: 'none',
                        minHeight: '60px',
                        outline: 'none',
                        fontFamily: 'inherit'
                      }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <button
                        onClick={() => handleFeedbackNote(message.id, message.dbId)}
                        style={{
                          background: '#2563eb',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '8px 12px',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setFeedbackNoteOpen(null);
                          setFeedbackNoteText('');
                        }}
                        style={{
                          background: '#f1f5f9',
                          color: '#64748b',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '8px 12px',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '16px',
              maxWidth: '900px'
            }}>
              <div style={{
                width: '36px',
                height: '36px',
                backgroundColor: '#dbeafe',
                color: '#2563eb',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Bot size={18} />
              </div>
              <div style={{
                backgroundColor: 'white',
                padding: '20px',
                borderRadius: '16px',
                borderTopLeftRadius: '4px',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
                border: '1px solid #f1f5f9',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: '#64748b',
                fontSize: '17px'
              }}>
                <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                <span>Thinking...</span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="input-area" style={{ padding: '32px 32px 40px 32px', paddingTop: '16px' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(input); }}>
              <div style={{ position: 'relative' }}>
                <div style={{
                  backgroundColor: 'white',
                  border: '2px solid #e2e8f0',
                  borderRadius: '24px',
                  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px',
                  transition: 'all 0.2s'
                }}>
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Type your question..."
                    disabled={loading}
                    style={{
                      flex: 1,
                      backgroundColor: 'transparent',
                      padding: '8px',
                      border: 'none',
                      outline: 'none',
                      color: '#334155',
                      fontSize: '18px'
                    }}
                  />
                  <button
                    type="submit"
                    disabled={loading || !input.trim()}
                    style={{
                      backgroundColor: loading || !input.trim() ? '#cbd5e1' : '#2563eb',
                      color: 'white',
                      padding: '12px',
                      borderRadius: '16px',
                      border: 'none',
                      cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
                      boxShadow: loading || !input.trim() ? 'none' : '0 10px 15px -3px rgba(37, 99, 235, 0.3)',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Send size={20} style={{ transform: 'rotate(45deg)' }} />
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </main>

      {/* Google Fonts & Animation */}
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Responsive Styles */
        @media (max-width: 768px) {
          /* Mobile: Hide sidebar by default, show as overlay when open */
          .sidebar {
            position: fixed !important;
            top: 0;
            left: 0;
            bottom: 0;
            transform: translateX(-100%);
            box-shadow: 2px 0 10px rgba(0, 0, 0, 0.3);
          }
          
          .sidebar.open {
            transform: translateX(0) !important;
          }
          
          .mobile-overlay {
            display: block !important;
          }
          
          .mobile-menu-btn {
            display: flex !important;
          }

          /* Adjust main content padding */
          main {
            width: 100%;
          }

          /* Smaller header on mobile */
          header {
            padding: 0 12px !important;
            height: 72px !important;
          }

          /* Hide partner logos on small screens */
          .partner-logos {
            display: none !important;
          }
          
          /* Hide topbar logos on small screens */
          .topbar-logos {
            display: none !important;
          }

          /* Make chat messages max-width full on mobile */
          .chat-messages > div {
            max-width: 100% !important;
          }

          /* Smaller avatar on mobile */
          .chat-messages > div > div:first-child {
            width: 32px !important;
            height: 32px !important;
          }

          /* Adjust message padding */
          .chat-messages > div > div > div {
            padding: 12px 14px !important;
          }
        }

        @media (max-width: 480px) {
          /* Stack RAG toggle buttons on very small screens */
          .rag-toggle {
            flex-direction: column !important;
            gap: 6px !important;
          }

          .rag-toggle button {
            width: 100%;
            justify-content: center !important;
          }

          /* Smaller title font on tiny screens */
          header h2 {
            font-size: 18px !important;
          }

          /* Smaller logo in sidebar */
          .sidebar img {
            height: 50px !important;
          }

          /* Make input bigger on small mobile for better UX */
          .input-area input {
            font-size: 16px !important; /* Prevents zoom on iOS */
          }
        }

        @media (min-width: 769px) and (max-width: 1024px) {
          /* Tablet: Narrower sidebar */
          .sidebar {
            width: 240px !important;
          }
        }

        @media (max-width: 768px) {
          /* Reduce chat message padding on mobile */
          .chat-messages {
            padding: 16px !important;
            gap: 24px !important;
          }

          /* Smaller input on mobile */
          .input-area {
            padding: 16px 16px 24px 16px !important;
          }

          /* Make feedback buttons touch-friendly */
          .feedback-buttons {
            gap: 6px !important;
          }

          .feedback-buttons button {
            padding: 8px !important;
            min-width: 32px;
            min-height: 32px;
          }

          /* Star rating touch-friendly */
          .star-rating {
            padding: 4px 8px !important;
            margin-left: 0 !important;
            margin-top: 6px;
          }

          .star-rating button {
            padding: 4px !important;
          }

          /* Suggested questions wrap better */
          .chat-messages button {
            font-size: 13px !important;
            padding: 8px 14px !important;
          }

          /* Better spacing for feedback section */
          .feedback-buttons {
            margin-top: 8px !important;
          }

          /* Close button in sidebar */
          .sidebar-close-btn {
            display: flex !important;
            position: absolute;
            top: 16px;
            right: 16px;
            z-index: 10;
            background: rgba(255, 255, 255, 0.1);
            border: none;
            padding: 8px;
            border-radius: 8px;
            cursor: pointer;
            color: white;
          }
        }

        @media (min-width: 769px) {
          /* Hide close button on desktop */
          .sidebar-close-btn {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};
